import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
    DialogTitle, Grid, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import ListIcon from '@mui/icons-material/List';
import {
    AI_LABELS, checkAiMcpServer, createAiMcpServer, deleteAiMcpServer, getAiMcpServerResources, getAiMcpServers, getAiMcpServerTools, getAiMeta,
    updateAiMcpServer,
} from '../../Api';
import {
    AI_DATA_CLASSES, AI_STATUSES, AiDataClass, AiMcpCheckDto, AiMcpResourceDto, AiMcpServerDto, AiMcpToolDto, AiMetaDto, AiStatus,
    AiUpsertMcpServerRequest,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import {
    AiActionError, AiConfiguredChip, AiDataClassChip, AiErrorAlert, aiLoadErrorText, AiStatusChip, CredentialProperty,
    EnumSelect, formatAiDate, JsonBlock, parseOptionalNumber, textError, toAiActionError,
} from '../../components/ai/AiShared';

interface ServerForm {
    code: string;
    name: string;
    url: string;
    authHeader: string;
    credentialRef: string;
    timeoutMs: string;
    dataClass: AiDataClass;
    status: AiStatus;
}

const CODE_PATTERN = /^[a-z0-9._-]+$/;

function ServerDialog({ open, server, meta, onClose, onSaved }: {
    open: boolean;
    server: AiMcpServerDto | null;
    meta: AiMetaDto | null;
    onClose: () => void;
    onSaved: (message: string) => void;
}) {
    const empty: ServerForm = { code: '', name: '', url: '', authHeader: 'Authorization', credentialRef: '', timeoutMs: '10000', dataClass: 'INTERNAL', status: 'ACTIVE' };
    const [form, setForm] = React.useState<ServerForm>(empty);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (!open) return;
        setError(null);
        setForm(server ? {
            code: server.code, name: server.name, url: server.url, authHeader: server.authHeader ?? '', credentialRef: server.credentialRef ?? '',
            timeoutMs: server.timeoutMs != null ? String(server.timeoutMs) : '', dataClass: server.dataClass, status: server.status,
        } : empty);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, server]);

    const save = async () => {
        if (!CODE_PATTERN.test(form.code)) return setError(textError('Code: lowercase letters, digits, ".", "_", "-".'));
        if (!form.name.trim() || !form.url.trim()) return setError(textError('Name and URL are required.'));
        const timeoutMs = parseOptionalNumber(form.timeoutMs);
        if (timeoutMs === undefined) return setError(textError('Timeout must be a number.'));
        const request: AiUpsertMcpServerRequest = {
            code: form.code, name: form.name.trim(), url: form.url.trim(), authHeader: form.authHeader.trim() || null,
            credentialRef: form.credentialRef.trim() || null, timeoutMs, dataClass: form.dataClass, status: form.status,
        };
        setSaving(true);
        const result = server ? await updateAiMcpServer(server.code, request) : await createAiMcpServer(request);
        setSaving(false);
        if (result.error != null) return setError(toAiActionError(result, 'Save failed'));
        onSaved(`MCP server ${request.code} saved.`);
    };

    const pattern = meta?.mcpCredentialPropertyPattern || 'betup.ai.mcp.credentials.<credentialRef>';
    const property = form.credentialRef.trim() ? pattern.replace('<credentialRef>', form.credentialRef.trim()) : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{server ? `Edit MCP server ${server.code}` : 'New MCP server'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={5}>
                        <TextField label="Code" fullWidth required value={form.code} disabled={server != null}
                            onChange={e => setForm(f => ({ ...f, code: e.target.value.toLowerCase() }))} placeholder="analytics-admin" />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <TextField label="Name" fullWidth required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField label="URL" fullWidth required value={form.url} placeholder="http://betup-server-analytics/mcp"
                            onChange={e => setForm(f => ({ ...f, url: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Auth header (optional)" fullWidth value={form.authHeader} onChange={e => setForm(f => ({ ...f, authHeader: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField label="Credential ref (optional)" fullWidth value={form.credentialRef} onChange={e => setForm(f => ({ ...f, credentialRef: e.target.value }))} />
                    </Grid>
                    {property && (
                        <Grid item xs={12}>
                            <Alert severity="info">
                                The header value is read from configuration property
                                <Box sx={{ mt: 0.5 }}><CredentialProperty property={server?.credentialProperty ?? property} /></Box>
                            </Alert>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={4}>
                        <TextField label="Timeout (ms)" fullWidth value={form.timeoutMs} onChange={e => setForm(f => ({ ...f, timeoutMs: e.target.value.replace(/\D/g, '') }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <EnumSelect label="Data class" value={form.dataClass} options={meta?.dataClasses ?? AI_DATA_CLASSES}
                            onChange={v => v && setForm(f => ({ ...f, dataClass: v }))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <EnumSelect label="Status" value={form.status} options={meta?.statuses ?? AI_STATUSES}
                            onChange={v => v && setForm(f => ({ ...f, status: v }))} />
                    </Grid>
                </Grid>
                <AiErrorAlert error={error} onClose={() => setError(null)} sx={{ mt: 2 }} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
            </DialogActions>
        </Dialog>
    );
}

/** What the server offers right now: its tools and its resources (e.g. context resources an agent reads before a run). */
function ToolsDialog({ server, onClose }: { server: AiMcpServerDto | null; onClose: () => void }) {
    const [tools, setTools] = React.useState<AiMcpToolDto[] | null>(null);
    const [resources, setResources] = React.useState<AiMcpResourceDto[] | null>(null);
    const [resourcesError, setResourcesError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [expanded, setExpanded] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (server == null) return;
        let cancelled = false;
        setLoading(true);
        setTools(null);
        setResources(null);
        setError(null);
        setResourcesError(null);
        Promise.all([getAiMcpServerTools(server.code), getAiMcpServerResources(server.code)]).then(([list, listed]) => {
            if (cancelled) return;
            setLoading(false);
            setTools(list);
            setResources(listed);
            if (list == null) setError(aiLoadErrorText(AI_LABELS.mcpTools, 'Failed to list tools.'));
            if (listed == null) setResourcesError(aiLoadErrorText(AI_LABELS.mcpResources, 'Failed to list resources.'));
        });
        return () => { cancelled = true; };
    }, [server]);

    return (
        <Dialog open={server != null} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Live tools &amp; resources — {server?.name} ({server?.code})</DialogTitle>
            <DialogContent>
                {loading && <CircularProgress size={24} />}
                {error && <Alert severity="error">{error}</Alert>}
                {tools && tools.length === 0 && <Typography color="text.secondary">The server lists no tools.</Typography>}
                {tools && tools.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell>Access</TableCell>
                                <TableCell>Schema</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tools.map(t => (
                                <React.Fragment key={t.name}>
                                    <TableRow sx={{ verticalAlign: 'top' }}>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{t.name}</Typography>
                                            {t.title && <Typography variant="caption" color="text.secondary">{t.title}</Typography>}
                                        </TableCell>
                                        <TableCell><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{t.description || '—'}</Typography></TableCell>
                                        <TableCell>
                                            {t.readOnly ? <Chip size="small" variant="outlined" label="read-only" /> : <Chip size="small" color="warning" variant="outlined" label="writes" />}
                                        </TableCell>
                                        <TableCell>
                                            {t.inputSchema ? <Button size="small" onClick={() => setExpanded(expanded === t.name ? null : t.name)}>{expanded === t.name ? 'Hide' : 'Show'}</Button> : '—'}
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ py: 0, borderBottom: expanded === t.name ? undefined : 'none' }}>
                                            <Collapse in={expanded === t.name} unmountOnExit><Box sx={{ py: 1 }}><JsonBlock value={t.inputSchema} /></Box></Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {(resources != null || resourcesError) && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1">Resources</Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            Read-only content the model never fetches itself; an agent can append one to its system prompt
                            as a context resource (agent › Prompt).
                        </Typography>
                        {resourcesError && <Alert severity="error">{resourcesError}</Alert>}
                        {resources && resources.length === 0 && <Typography color="text.secondary">The server lists no resources.</Typography>}
                        {resources && resources.length > 0 && (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>URI</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Type</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {resources.map(r => (
                                        <TableRow key={r.uri} sx={{ verticalAlign: 'top' }}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{r.uri}</Typography>
                                                {(r.title || r.name) && <Typography variant="caption" color="text.secondary">{r.title || r.name}</Typography>}
                                            </TableCell>
                                            <TableCell><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{r.description || '—'}</Typography></TableCell>
                                            <TableCell>{r.mimeType ? <Chip size="small" variant="outlined" label={r.mimeType} /> : '—'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
}

export default function AiMcpServersPage() {
    const [meta, setMeta] = React.useState<AiMetaDto | null>(null);
    const [servers, setServers] = React.useState<AiMcpServerDto[]>([]);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [checks, setChecks] = React.useState<Record<string, AiMcpCheckDto | 'running'>>({});
    const [dialog, setDialog] = React.useState<{ open: boolean; server: AiMcpServerDto | null }>({ open: false, server: null });
    const [toolsServer, setToolsServer] = React.useState<AiMcpServerDto | null>(null);
    const [pendingDelete, setPendingDelete] = React.useState<AiMcpServerDto | null>(null);

    const load = React.useCallback(async () => {
        const [m, s] = await Promise.all([getAiMeta(), getAiMcpServers()]);
        setMeta(m);
        setServers(s ?? []);
        setLoadError(s == null ? aiLoadErrorText(AI_LABELS.mcpServers, 'Failed to load MCP servers.') : null);
    }, []);
    React.useEffect(() => { load(); }, [load]);

    const check = async (server: AiMcpServerDto) => {
        setChecks(c => ({ ...c, [server.code]: 'running' }));
        const result = await checkAiMcpServer(server.code);
        if (result.error != null || result.data == null) {
            setChecks(c => {
                const next = { ...c };
                delete next[server.code];
                return next;
            });
            setError(toAiActionError(result, `Check of ${server.code} failed`) ?? textError('Empty response'));
            return;
        }
        const data = result.data;
        setChecks(c => ({ ...c, [server.code]: data }));
        load();
    };

    const remove = async () => {
        if (pendingDelete == null) return;
        const target = pendingDelete;
        setPendingDelete(null);
        const result = await deleteAiMcpServer(target.code);
        if (result.error != null) {
            setError(toAiActionError(result, 'Delete failed'));
            return;
        }
        setNotice(`MCP server ${target.code} deleted.`);
        load();
    };

    return (
        <Stack gap={2} sx={{ mx: 5, my: 5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                    <Typography variant="h4">MCP servers</Typography>
                    <Typography variant="body2" color="text.secondary">Remote tool servers agents can be linked to. A server still linked to an agent cannot be deleted (409).</Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, server: null })}>New MCP server</Button>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
            <Card variant="outlined">
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Server</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell>Credential</TableCell>
                                <TableCell>Data class</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Last OK / error</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {servers.length === 0 && <TableRow><TableCell colSpan={7}><Typography variant="body2" color="text.secondary">No MCP servers.</Typography></TableCell></TableRow>}
                            {servers.map(s => {
                                const c = checks[s.code];
                                return (
                                    <React.Fragment key={s.code}>
                                        <TableRow sx={{ verticalAlign: 'top' }}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{s.code}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 260, wordBreak: 'break-all' }}>
                                                <Typography variant="body2">{s.url}</Typography>
                                                <Typography variant="caption" color="text.secondary">timeout {s.timeoutMs ?? '—'} ms</Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 320 }}>
                                                {s.credentialRef ? (
                                                    <>
                                                        <Typography variant="caption" color="text.secondary">header {s.authHeader ?? '—'}</Typography>
                                                        <CredentialProperty property={s.credentialProperty} />
                                                        <AiConfiguredChip configured={s.configured} />
                                                    </>
                                                ) : <Typography variant="body2" color="text.secondary">none</Typography>}
                                            </TableCell>
                                            <TableCell><AiDataClassChip dataClass={s.dataClass} /></TableCell>
                                            <TableCell><AiStatusChip status={s.status} /></TableCell>
                                            <TableCell sx={{ maxWidth: 260 }}>
                                                <Typography variant="caption" display="block" color="success.main">ok {formatAiDate(s.lastOkAt)}</Typography>
                                                {s.lastError && (
                                                    <Tooltip title={s.lastError}>
                                                        <Typography variant="caption" display="block" color="error" sx={{ wordBreak: 'break-word' }}>
                                                            {formatAiDate(s.lastErrorAt)}: {s.lastError}
                                                        </Typography>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Button size="small" startIcon={c === 'running' ? <CircularProgress size={14} /> : <NetworkCheckIcon />}
                                                    disabled={c === 'running'} onClick={() => check(s)}>Check</Button>
                                                <Button size="small" startIcon={<ListIcon />} onClick={() => setToolsServer(s)}>Tools</Button>
                                                <Tooltip title="Edit"><IconButton size="small" onClick={() => setDialog({ open: true, server: s })}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setPendingDelete(s)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                            </TableCell>
                                        </TableRow>
                                        {c != null && c !== 'running' && (
                                            <TableRow>
                                                <TableCell colSpan={7}>
                                                    <Alert severity={c.ok ? 'success' : 'error'} onClose={() => setChecks(all => {
                                                        const next = { ...all };
                                                        delete next[s.code];
                                                        return next;
                                                    })}>
                                                        {c.ok ? `Check OK — ${c.toolCount ?? 0} tool(s)` : `Check failed: ${c.error ?? 'unknown error'}`} · {formatAiDate(c.checkedAt)}
                                                    </Alert>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
            <ServerDialog open={dialog.open} server={dialog.server} meta={meta} onClose={() => setDialog({ open: false, server: null })}
                onSaved={message => { setDialog({ open: false, server: null }); setNotice(message); setError(null); load(); }} />
            <ToolsDialog server={toolsServer} onClose={() => setToolsServer(null)} />
            <ConfirmDialog
                open={pendingDelete != null}
                title="Delete MCP server?"
                text={<>MCP server <b>{pendingDelete?.code}</b> will be deleted. Fails (409) while agents are linked to it.</>}
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setPendingDelete(null)}
                onConfirm={remove}
            />
        </Stack>
    );
}
